import React, { useEffect, useRef, useState } from "react"
import { createParentBridge } from "../parent/parent-bridge"
import { BRIDGE_PROTOCOL } from "../shared/constants"

/**
 * ParentPlayground
 *
 * This component acts as the HOST application.
 * It renders an iframe and attaches the Parent Iframe Bridge
 * to receive navigation events from the child iframe.
 */
export function ParentPlayground() {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Store incoming events for debugging / visualization
  const [logs, setLogs] = useState<string[]>([])

  useEffect(() => {
    /**
     * Create the parent bridge.
     * In playground mode we use same-origin to simplify setup.
     */
    const bridge = createParentBridge({
      // Only allow messages from the same origin (playground)
      allowedChildOrigins: [window.location.origin],

      // Target origin used when replying back to the child
      childTargetOrigin: window.location.origin,

      // How the bridge resolves the child window (iframe.contentWindow)
      childWindowGetter: () => iframeRef.current?.contentWindow ?? null,

      /**
       * Central event handler for all messages coming from the child.
       * In real apps, this is where you connect to router / map / navigation logic.
       */
      onEvent: async (msg, meta) => {
        setLogs((prev) => [
          `[Parent] type=${msg.type} origin=${
            meta.origin
          } payload=${JSON.stringify(msg.payload)}`,
          ...prev,
        ])
      },
    })

    // Attach message listener
    bridge.attach()

    // Cleanup on unmount
    return () => bridge.detach()
  }, [])

  // Child iframe URL (same-origin playground)
  const childUrl = `/iframe-bridge/playground/child?protocol=${encodeURIComponent(
    BRIDGE_PROTOCOL.CHANNEL
  )}`

  return (
    <div
      style={{
        padding: 16,
        display: "grid",
        gridTemplateColumns: "1fr 420px",
        gap: 12,
      }}
    >
      {/* Iframe container */}
      <div
        style={{
          border: "1px solid #333",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: 12, borderBottom: "1px solid #333" }}>
          <b>Parent Playground</b> (Host)
        </div>

        <iframe
          ref={iframeRef}
          src={childUrl}
          title="Iframe Bridge Child Playground"
          style={{ width: "100%", height: 560, border: 0 }}
        />
      </div>

      {/* Event log panel */}
      <div
        style={{
          border: "1px solid #333",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: 12, borderBottom: "1px solid #333" }}>
          <b>Logs</b>
        </div>

        <div
          style={{
            padding: 12,
            fontFamily: "monospace",
            fontSize: 12,
            maxHeight: 560,
            overflow: "auto",
          }}
        >
          {logs.length === 0 ? (
            <div style={{ opacity: 0.7 }}>No events received yet…</div>
          ) : (
            logs.map((line, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                {line}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
